/**
 * Small trailing-edge debounce helper.
 *
 * Collapses a burst of rapid calls into a SINGLE invocation that fires
 * `wait` ms after the last call. Used to tame the `omnis:*` refetch storms:
 * documentService.js dispatches one window event per row (e.g. one
 * `omnis:expense-created` per receipt in a 5-receipt photo, one
 * `omnis:ticket-updated` per ticket in a batch), and each panel listener does
 * a FULL refetch. Wrapping the refetch in a trailing debounce means N rapid
 * events → one refetch.
 *
 * The returned function carries a `.cancel()` that clears any pending timer —
 * call it from a React effect's cleanup so a component that unmounts mid-burst
 * never fires a setState-after-unmount.
 *
 * @param {Function} fn   the function to debounce
 * @param {number}   wait milliseconds to wait after the last call (default 300)
 * @returns {Function & { cancel: () => void }}
 */
export function debounce(fn, wait = 300) {
    let timer = null;
    const debounced = (...args) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            fn(...args);
        }, wait);
    };
    debounced.cancel = () => {
        if (timer) {
            clearTimeout(timer);
            timer = null;
        }
    };
    return debounced;
}

export default debounce;
